import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlugZap, RefreshCw, AlertTriangle, ShieldCheck, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { syncState, useTally } from "@/context/TallyContext";
import { monthLabel, timeAgo } from "@/lib/format";
import { dateLabel, drcr, homePeriods, money, useBooks } from "@/lib/books";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loaded } from "@/components/books/ui";
import { useFyMonth } from "@/components/books/Statement";
import {
  CashBalanceChart, IncomeExpenseChart, Key, RankedBars, SERIES_1, SERIES_2, STATUS_SERIOUS, SplitBar,
} from "@/components/books/charts";

function Card({ title, right, children, className = "" }) {
  return (
    <section className={`rounded-lg border border-slate-200 bg-white ${className}`}>
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

const CHART_PERIODS = ["this_fy", "last_fy", "last_12", "last_6"];
const ALL_PERIODS = ["this_fy", "this_quarter", "this_month", "last_fy", "last_quarter", "last_month", "last_6", "last_12"];

/** The period a card shows, remembered per card in this browser. */
function useCardPeriod(card, options) {
  const fyMonth = useFyMonth();
  const periods = useMemo(() => homePeriods(fyMonth), [fyMonth]);
  const storageKey = `home.period.${card}`;
  const [key, setKey] = useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      return options.includes(saved) ? saved : options[0];
    } catch {
      return options[0];
    }
  });
  const choose = (k) => {
    setKey(k);
    try { window.localStorage.setItem(storageKey, k); } catch { /* storage blocked: keep for this visit */ }
  };
  const p = periods[key];
  return { key, choose, label: p.label, params: { date_from: p.from, date_to: p.to }, periods };
}

/** "This Financial Year ⌄" in a card header, opening the card's periods. */
function PeriodMenu({ period, options, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={testid} aria-haspopup="listbox" aria-expanded={open}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-sm text-slate-600 hover:text-slate-900">
        {period.label}
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul role="listbox" className="absolute right-0 top-full z-30 mt-1 w-52 rounded-md border border-slate-200 bg-white p-1 shadow-lg">
          {options.map((k) => (
            <li key={k}>
              <button type="button" role="option" aria-selected={k === period.key}
                onClick={() => { period.choose(k); setOpen(false); }}
                className={`w-full rounded px-3 py-1.5 text-left text-sm ${k === period.key
                  ? "bg-[#408dfb] text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                {period.periods[k].label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CashFlowCard() {
  const period = useCardPeriod("cash_flow", CHART_PERIODS);
  const state = useBooks("/books/overview/cash-flow", period.params);
  const cf = state.data;
  return (
    <Card title="Cash flow" right={<PeriodMenu period={period} options={CHART_PERIODS} testid="cash-flow-period" />}>
      <Loaded state={state}>
        {cf && (
          <div className="grid gap-6 lg:grid-cols-[1fr_220px]">
            <CashBalanceChart months={cf.months} />
            <dl className="space-y-4 text-right">
              <div><dt className="text-xs text-slate-500">Cash as on {dateLabel(cf.from)}</dt><dd className="text-lg font-semibold">{money(cf.opening)}</dd></div>
              <div><dt className="text-xs text-slate-500">Incoming</dt><dd className="text-lg font-semibold">{money(cf.incoming)} <span className="text-sm font-normal text-slate-500">(+)</span></dd></div>
              <div><dt className="text-xs text-slate-500">Outgoing</dt><dd className="text-lg font-semibold">{money(cf.outgoing)} <span className="text-sm font-normal text-slate-500">(−)</span></dd></div>
              <div><dt className="text-xs text-slate-500">Cash as on {dateLabel(cf.closing_on)}</dt><dd className="text-lg font-semibold">{money(cf.closing)}</dd></div>
            </dl>
          </div>
        )}
      </Loaded>
    </Card>
  );
}

function IncomeExpenseCard() {
  const period = useCardPeriod("income_expense", CHART_PERIODS);
  const state = useBooks("/books/overview/income-expense", period.params);
  const ie = state.data;
  return (
    <Card title="Income and expense" right={<PeriodMenu period={period} options={CHART_PERIODS} testid="income-expense-period" />}>
      <Loaded state={state}>
        {ie && (
          <>
            <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1">
              <Key color={SERIES_1} label="Total income" value={money(ie.income)} />
              <Key color={SERIES_2} label="Total expenses" value={money(ie.expense)} />
            </div>
            <IncomeExpenseChart months={ie.months} />
            <p className="mt-2 text-xs text-slate-500">Income and expense exclude taxes, as in Tally's Profit & Loss.</p>
          </>
        )}
      </Loaded>
    </Card>
  );
}

function TopExpensesCard() {
  const navigate = useNavigate();
  const period = useCardPeriod("top_expenses", ALL_PERIODS);
  const state = useBooks("/books/overview/top-expenses", period.params);
  return (
    <Card title="Top expenses" right={<PeriodMenu period={period} options={ALL_PERIODS} testid="top-expenses-period" />}>
      <Loaded state={state}>
        {state.data && (state.data.rows.length ? (
          <RankedBars rows={state.data.rows} onClick={(r) => navigate(`/accounts/${r.guid}`)} />
        ) : (
          <div className="py-16 text-center text-sm text-slate-500">No expense recorded for {period.label.toLowerCase()}</div>
        ))}
      </Loaded>
    </Card>
  );
}

function OwedCard({ title, label, data, to }) {
  return (
    <Card title={title} right={<Link to={to} className="text-xs text-blue-600 hover:underline">View details</Link>}>
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{money(data.total)}</div>
      <div className="mt-4"><SplitBar current={data.current} overdue={data.overdue} /></div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1">
        <Key color={SERIES_1} label="Current" value={money(data.current)} />
        <Key color={STATUS_SERIOUS} label="Overdue" value={money(data.overdue)} />
      </div>
    </Card>
  );
}

function Dashboard() {
  const state = useBooks("/books/overview");
  return (
    <Loaded state={state}>
      {state.data && (() => {
        const d = state.data;
        return (
          <div className="space-y-5">
            {d.check && d.check.ok === false && (
              <Link to="/connect" className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
                <AlertTriangle size={16} className="shrink-0" />
                {d.check.mismatch_count === 1 ? "1 ledger balance differs" : `${d.check.mismatch_count} ledger balances differ`} from Tally
                (checked {dateLabel(d.check.as_of)}). SyncBooks is re-checking the vouchers behind them.
              </Link>
            )}
            <div className="grid gap-5 lg:grid-cols-2">
              <OwedCard title="Total receivables" label="Total unpaid invoices" data={d.receivables} to="/reports/ar-aging-summary" />
              <OwedCard title="Total payables" label="Total unpaid bills" data={d.payables} to="/reports/ap-aging-summary" />
            </div>

            <CashFlowCard />

            <div className="grid gap-5 lg:grid-cols-2">
              <IncomeExpenseCard />
              <TopExpensesCard />
            </div>

            <Card title="Bank and cash">
              {d.bank_accounts.length ? (
                <div className="divide-y divide-slate-100">
                  {d.bank_accounts.map((b) => (
                    <Link key={b.guid} to={`/accounts/${b.guid}`} className="flex items-center justify-between py-2.5 text-sm hover:text-blue-700">
                      <span>{b.name} <span className="ml-2 text-xs text-slate-400">{b.kind === "Cash-in-Hand" ? "Cash" : "Bank"}</span></span>
                      <span className="font-medium tabular-nums">{drcr(b.balance)}</span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-slate-500">No bank or cash ledgers in Tally.</div>
              )}
            </Card>
            {d.check?.ok && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck size={14} className="text-emerald-600" /> All {d.check.compared} ledger balances match Tally (checked {dateLabel(d.check.as_of)}).
              </div>
            )}
          </div>
        );
      })()}
    </Loaded>
  );
}

/** Home: the way to connect, the first load's progress, then the dashboard. */
export default function Home() {
  const { user, organization } = useAuth();
  const { company, loading } = useTally();
  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  const state = syncState(company);
  const load = company?.initial_load || {};
  const firstLoadDone = company?.initial_load_done_at || load.status === "DONE";

  return (
    <div className="space-y-5" data-testid="home-page">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Hello, {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="mt-0.5 text-sm text-slate-500">{company?.name || organization?.name}</p>
        </div>
        {company && firstLoadDone && (
          <div className="text-xs text-slate-500">Figures as in Tally · last change {timeAgo(company.last_batch_at)}</div>
        )}
      </div>

      {state === "NOT_CONNECTED" && (
        <div className="flex flex-col items-start gap-4 rounded-lg border border-slate-200 bg-gradient-to-br from-blue-50 to-white p-8">
          <PlugZap size={28} className="text-[#408dfb]" />
          <div>
            <div className="text-lg font-semibold">Connect your Tally to see your books here</div>
            <p className="mt-1 max-w-xl text-sm text-slate-600">
              Install the SyncBooks agent on the computer that runs TallyPrime and pick your company.
              Your ledgers, vouchers and stock then stay up to date here, as you work in Tally.
            </p>
          </div>
          <Link to="/connect"><Button className="bg-[#408dfb] hover:bg-[#2f7bea]">Connect Tally</Button></Link>
        </div>
      )}

      {company && !firstLoadDone && (
        <div className="max-w-2xl space-y-3 rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-2 font-medium"><RefreshCw size={16} className="animate-spin text-blue-600" /> Reading {company.name} from Tally</div>
          <Progress value={load.months_total ? (load.months_done / load.months_total) * 100 : 0} className="h-2" />
          <div className="text-sm text-slate-500">
            {load.months_done || 0} of {load.months_total || "?"} months
            {load.current_month
              ? load.current_month > new Date().toISOString().slice(0, 7)
                ? " · checking for post-dated vouchers"
                : ` · now reading ${monthLabel(load.current_month)}`
              : ""}
            . The dashboard appears when the first load is done.
          </div>
        </div>
      )}

      {company && firstLoadDone && (state === "AGENT_OFFLINE" || state === "COMPANY_CLOSED") && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            {state === "AGENT_OFFLINE"
              ? "The Tally computer is offline, so these figures may be behind Tally."
              : `${company.name} is not open in TallyPrime, so these figures may be behind Tally.`}
            {" "}Last change received {timeAgo(company.last_batch_at)}.
          </div>
        </div>
      )}

      {company && firstLoadDone && <Dashboard />}
    </div>
  );
}
